import { Head, Link, router, usePage } from '@inertiajs/react'
import { useState, useEffect } from 'react'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import Layout from '../../components/Layout'
import AgendaSectionRow from '../../components/agenda_templates/AgendaSectionRow'
import AddCustomSectionModal from '../../components/agenda_templates/AddCustomSectionModal'
import type { AgendaTemplate, AgendaTemplateSection, OrgTeamOption } from '../../types'

interface Props {
  template: AgendaTemplate & { sections: AgendaTemplateSection[]; org_teams: OrgTeamOption[]; assigned_team_ids: number[] }
  can_edit: boolean
  can_copy?: boolean
  active_tab: string
}

export default function AgendaTemplateShow({ template, can_edit, can_copy, active_tab }: Props) {
  const { flash } = usePage().props as any
  const [sections, setSections] = useState(
    [...template.sections].sort((a, b) => a.position - b.position)
  )
  const [templateName, setTemplateName] = useState(template.name)
  const [showAddModal, setShowAddModal] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const [assignedTeamIds, setAssignedTeamIds] = useState<number[]>(template.assigned_team_ids || [])

  useEffect(() => {
    const serverSections = [...template.sections].sort((a, b) => a.position - b.position)
    if (!isDirty) {
      setSections(serverSections)
      setTemplateName(template.name)
    } else {
      setSections(prev => {
        const localById = new Map(prev.map(s => [s.id, s]))
        const serverIds = new Set(serverSections.map(s => s.id))
        const merged = prev.filter(s => serverIds.has(s.id))
        const newSections = serverSections.filter(s => !localById.has(s.id))
        return [...merged, ...newSections]
      })
    }
  }, [template.sections, template.name])
  const [isAssigning, setIsAssigning] = useState(false)
  const [showCopyModal, setShowCopyModal] = useState(false)
  const [copyTeamId, setCopyTeamId] = useState<number | ''>('')
  const [isCopying, setIsCopying] = useState(false)

  const handleCopyToTeam = () => {
    if (copyTeamId === '') return
    setIsCopying(true)
    router.post(
      `/agenda_templates/${template.id}/copy_to_team`,
      { team_id: copyTeamId },
      {
        onSuccess: () => {
          setShowCopyModal(false)
          setCopyTeamId('')
        },
        onFinish: () => setIsCopying(false)
      }
    )
  }

  const totalMinutes = sections
    .filter(s => s.is_enabled && !s.is_heading)
    .reduce((sum, s) => sum + (s.duration_minutes || 0), 0)

  const hours = Math.floor(totalMinutes / 60)
  const mins = totalMinutes % 60
  const durationLabel = hours > 0
    ? `${hours}h ${mins > 0 ? `${mins}m` : ''}`
    : `${mins} min`

  const handleDragEnd = (result: any) => {
    if (!result.destination) return
    const items = Array.from(sections)
    const [moved] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, moved)
    setSections(items.map((s, i) => ({ ...s, position: i + 1 })))
    setIsDirty(true)
  }

  const handleDurationChange = (id: number, minutes: number) => {
    setSections(prev => prev.map(s =>
      s.id === id ? { ...s, duration_minutes: Math.max(1, Math.min(120, minutes)) } : s
    ))
    setIsDirty(true)
  }

  const handleSectionTitleChange = (id: number, title: string) => {
    setSections(prev => prev.map(s =>
      s.id === id ? { ...s, title } : s
    ))
    setIsDirty(true)
  }

  const handleTemplateNameChange = (value: string) => {
    setTemplateName(value)
    setIsDirty(true)
  }

  const handleToggle = (id: number) => {
    setSections(prev => prev.map(s =>
      s.id === id ? { ...s, is_enabled: !s.is_enabled } : s
    ))
    setIsDirty(true)
  }

  const handleSave = () => {
    setIsSaving(true)
    router.put(
      `/agenda_templates/${template.id}`,
      {
        agenda_template: {
          name: templateName.trim(),
          sections: sections.map(s => ({
            id: s.id,
            position: s.position,
            duration_minutes: s.duration_minutes,
            is_enabled: s.is_enabled,
            title: s.title,
            custom_notes: s.custom_notes || ''
          }))
        }
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          setIsDirty(false)
        },
        onFinish: () => {
          setIsSaving(false)
        }
      }
    )
  }

  const handleTeamToggle = (teamId: number) => {
    setAssignedTeamIds(prev =>
      prev.includes(teamId) ? prev.filter(id => id !== teamId) : [...prev, teamId]
    )
  }

  const handleAssignTeams = () => {
    setIsAssigning(true)
    router.put(
      `/agenda_templates/${template.id}/assign_teams`,
      { team_ids: assignedTeamIds },
      { onFinish: () => setIsAssigning(false) }
    )
  }

  return (
    <Layout activeTab={active_tab}>
      <Head title={`Edit Agenda — ${templateName}`} />
      <div className="max-w-2xl mx-auto px-6 py-8">

        <nav className="text-sm mb-6 text-text-muted">
          <Link href="/agenda_templates" className="hover:opacity-80">Agendas</Link>
          <span className="mx-2">›</span>
          <span>{templateName}</span>
        </nav>

        {flash?.notice && (
          <div className="mb-4 px-4 py-3 bg-[rgba(21,83,46,0.15)] border border-[#15532E]/20 rounded text-sm text-[#15532E]">
            {flash.notice}
          </div>
        )}
        {flash?.alert && (
          <div className="mb-4 px-4 py-3 bg-[rgba(237,28,36,0.15)] border border-[#ED1C24]/20 rounded text-sm text-[#ED1C24]">
            {flash.alert}
          </div>
        )}

        {assignedTeamIds.length === 0 && (
          <div className="mb-4 flex items-start gap-3 px-4 py-3 bg-status-at-risk-bg border border-status-at-risk/30 rounded text-sm text-status-at-risk">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <div>
              <p className="font-semibold">Not assigned to any team</p>
              <p className="mt-0.5">
                This template won't be used in any meeting until you assign it to a team.
                {can_edit && (template.org_teams || []).length > 0
                  ? ' Choose one or more teams in the “Assigned Teams” section below.'
                  : ''}
              </p>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 mb-6">
          <div className="flex-1 min-w-[16rem]">
            <div className="flex items-center gap-2">
              {can_edit ? (
                <input
                  type="text"
                  value={templateName}
                  onChange={e => handleTemplateNameChange(e.target.value)}
                  className="text-2xl font-semibold text-text-primary bg-transparent border-b-2 border-transparent hover:border-stone focus:border-charcoal focus:outline-none px-1 -ml-1 max-w-md"
                  aria-label="Template display name"
                />
              ) : (
                <h1 className="text-2xl font-semibold text-text-primary">{templateName}</h1>
              )}
              {template.is_required && (
                <span
                  className="text-xs font-semibold uppercase tracking-wide text-text-primary bg-marigold/30 border border-marigold/50 px-2 py-0.5 rounded-full"
                  title="This template is required and cannot be deleted"
                >
                  Required
                </span>
              )}
            </div>
            {template.description && (
              <p className="text-sm text-text-secondary mt-1">{template.description}</p>
            )}
            <div className="flex items-center gap-2 mt-2 text-sm text-text-muted">
              <span>{sections.length} sections</span>
              <span>·</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              <span className="font-medium">{durationLabel} total</span>
            </div>
          </div>
          {can_edit && (
            <div className="flex flex-wrap gap-3">
              {can_copy && (template.org_teams || []).length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowCopyModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-text-primary bg-white border border-stone rounded hover:bg-[#F8F9FA] transition-colors"
                  title="Copy this template to another team"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                  Copy to Team
                </button>
              )}
              {template.can_delete && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Delete the "${templateName}" agenda template? This cannot be undone.`)) {
                      router.delete(`/agenda_templates/${template.id}`)
                    }
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-pinnacle-red bg-white border border-pinnacle-red/40 rounded hover:bg-pinnacle-red/5 transition-colors"
                  aria-label="Delete template"
                  title="Delete template"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                    <path d="M10 11v6"/><path d="M14 11v6"/>
                    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
                  </svg>
                  Delete
                </button>
              )}
              <Link
                href="/agenda_templates"
                className="px-4 py-2 text-sm font-medium text-text-primary bg-white border border-stone rounded hover:bg-[#F8F9FA] transition-colors"
              >
                Cancel
              </Link>
              <button
                onClick={handleSave}
                disabled={!isDirty || isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-pinnacle-red rounded hover:bg-pinnacle-red-dark transition-colors disabled:opacity-40"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-stone overflow-hidden mb-6">
          <div className="px-5 py-3 border-b border-stone">
            <span className="text-sm font-bold uppercase tracking-widest text-text-muted">SECTIONS</span>
          </div>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="sections">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {sections.map((section, index) => (
                    <Draggable
                      key={section.id}
                      draggableId={String(section.id)}
                      index={index}
                      isDragDisabled={!can_edit}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={snapshot.isDragging ? 'shadow-lg bg-white' : ''}
                        >
                          <AgendaSectionRow
                            section={section}
                            index={index}
                            canEdit={can_edit}
                            templateRequiredKind={template.required_kind}
                            onTitleChange={handleSectionTitleChange}
                            onDurationChange={handleDurationChange}
                            onToggle={handleToggle}
                            dragHandleProps={provided.dragHandleProps}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {can_edit && (
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 w-full px-5 py-4 text-text-primary text-sm font-semibold border-t border-dashed border-stone hover:bg-charcoal/5 transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add Section
            </button>
          )}
        </div>

        {can_edit && template.is_required && (
          <div className="bg-white rounded-lg shadow-sm border border-stone overflow-hidden mb-6">
            <div className="px-5 py-3 border-b border-stone">
              <span className="text-sm font-bold uppercase tracking-widest text-text-muted">ASSIGNED TEAMS</span>
            </div>
            <div className="px-5 py-4 text-sm text-text-secondary">
              This template is required, so it is automatically assigned to every team in your organization.
            </div>
          </div>
        )}

        {can_edit && !template.is_required && (template.org_teams || []).length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-stone overflow-hidden mb-6">
            <div className="px-5 py-3 border-b border-stone">
              <span className="text-sm font-bold uppercase tracking-widest text-text-muted">ASSIGNED TEAMS</span>
            </div>
            <div className="px-5 py-4 space-y-3">
              {(template.org_teams || []).map(team => (
                <label key={team.id} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={assignedTeamIds.includes(team.id)}
                    onChange={() => handleTeamToggle(team.id)}
                    className="w-4 h-4 rounded border-stone text-pinnacle-red focus:ring-pinnacle-red"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-text-primary group-hover:underline transition-colors">
                      {team.name}
                    </span>
                    <span className="text-sm text-text-muted ml-2">
                      {team.member_count} member{team.member_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                </label>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-stone bg-[#F8F9FA] flex justify-end">
              <button
                onClick={handleAssignTeams}
                disabled={isAssigning}
                className="px-4 py-2 text-sm font-medium text-white bg-pinnacle-red rounded hover:bg-pinnacle-red-dark transition-colors disabled:opacity-40"
              >
                {isAssigning ? 'Saving...' : 'Save Team Assignments'}
              </button>
            </div>
          </div>
        )}
      </div>

      {showCopyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => !isCopying && setShowCopyModal(false)}>
          <div
            className="bg-white rounded-lg shadow-xl border border-stone w-full max-w-md mx-4"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Copy template to team"
          >
            <div className="px-5 py-4 border-b border-stone">
              <h2 className="font-semibold text-text-primary">Copy “{templateName}” to a team</h2>
              <p className="text-sm text-text-secondary mt-1">
                Creates a duplicate of this template (sections and durations included) assigned to the team you choose.
              </p>
            </div>
            <div className="px-5 py-4">
              <label className="block text-sm font-medium text-text-primary mb-1" htmlFor="copy-team-select">
                Team
              </label>
              <select
                id="copy-team-select"
                value={copyTeamId}
                onChange={e => setCopyTeamId(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full border border-stone rounded px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-charcoal"
              >
                <option value="">Choose a team…</option>
                {(template.org_teams || []).map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </div>
            <div className="px-5 py-3 border-t border-stone bg-[#F8F9FA] flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCopyModal(false)}
                disabled={isCopying}
                className="px-4 py-2 text-sm font-medium text-text-primary bg-white border border-stone rounded hover:bg-[#F8F9FA] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCopyToTeam}
                disabled={copyTeamId === '' || isCopying}
                className="px-4 py-2 text-sm font-medium text-white bg-pinnacle-red rounded hover:bg-pinnacle-red-dark transition-colors disabled:opacity-40"
              >
                {isCopying ? 'Copying…' : 'Copy Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <AddCustomSectionModal
          templateId={template.id}
          existingSections={sections}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </Layout>
  )
}
